import { useState, useRef, useEffect, useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type {
  FormDescriptor,
  ActionDescriptor,
  FormSubmissionResult,
  FormSubmissionContext,
  FormErrorDescriptor,
  SubmissionState,
  TranslationKey,
} from '@finance/contracts';
import type { ActionExecutionContext } from '../actions/action-executor';
import { resolveRuntimeTKey } from '../../i18n/resolve-key';
import { buildFormPayload } from './build-form-payload';
import { applyInputTransform } from './value-transformer';
import type { ResolvedAction } from './action-resolution';
import { normalizeDateInput, normalizeDateTimeInput } from '../dates/date-semantics';

const UNEXPECTED_ERROR_KEY = 'common:error.unexpected' as TranslationKey;

/**
 * Runtime/server error codes are displayable only when they are members of the
 * compiled catalog. Raw exception/database/service text never becomes UI copy.
 */
export function canonicalFormErrorKey(value: unknown): TranslationKey {
  if (typeof value === 'string' && resolveRuntimeTKey(value)) return value as TranslationKey;
  return UNEXPECTED_ERROR_KEY;
}

export function sanitizeFormErrorDescriptor(
  value: FormErrorDescriptor | undefined | null,
): FormErrorDescriptor {
  return {
    code: canonicalFormErrorKey(value?.code),
    ...(value?.params ? { params: value.params } : {}),
    ...(value?.correlationId ? { correlationId: value.correlationId } : {}),
    ...(value?.retryable !== undefined ? { retryable: value.retryable } : {}),
  };
}

export interface UseFormSubmissionOptions {
  contract: FormDescriptor;
  formMethods: Pick<
    UseFormReturn<Record<string, unknown>>,
    'trigger' | 'getValues' | 'setError' | 'clearErrors' | 'reset'
  >;
  resolvedActions: ResolvedAction[];
  fieldStates: Map<string, import('./field-state').ResolvedFieldState>;
  onSubmit?: (
    commandId: string,
    values: Record<string, unknown>,
    context: FormSubmissionContext
  ) => Promise<FormSubmissionResult>;
  onAction?: (
    action: ActionDescriptor,
    context?: ActionExecutionContext,
  ) => void | Promise<void>;
  serverErrors?: FormErrorDescriptor[] | Record<string, string> | null;
  initialValues?: Record<string, unknown>;
  recordVersion?: number;
  defaultValues: Record<string, unknown>;
  loadingInitialValues?: boolean;
  formValuesWithIds: Record<string, unknown>;
}

export interface UseFormSubmissionResult {
  submitAction: (actionId: string, context?: ActionExecutionContext) => Promise<void>;
  handleAction: (action: ActionDescriptor, context?: ActionExecutionContext) => Promise<void>;
  submissionState: SubmissionState;
  submissionErrors: {
    fieldErrors: Record<string, FormErrorDescriptor>;
    globalErrors: FormErrorDescriptor[];
    submissionError: FormErrorDescriptor | null;
    conflict: { currentVersion?: string } | null;
  };
  clearConflict: () => void;
}

/**
 * Convert ISO datetime strings to the format expected by HTML date/datetime-local inputs.
 * HTML date inputs require `yyyy-MM-dd`; datetime-local inputs require `yyyy-MM-ddTHH:mm`.
 */
function normalizeDateValueForInput(
  value: unknown,
  fieldType: string,
): unknown {
  return fieldType === 'date' ? normalizeDateInput(value) : normalizeDateTimeInput(value);
}

export function normalizeInitialFormValues(
  fields: FormDescriptor['fields'],
  defaultValues: Record<string, unknown>,
  initialValues: Record<string, unknown>
): Record<string, unknown> {
  const normalized = { ...initialValues };
  for (const field of fields) {
    if (
      field.name === 'expectedVersion' &&
      normalized.expectedVersion === undefined &&
      typeof normalized.version === 'number' &&
      Number.isSafeInteger(normalized.version) &&
      normalized.version >= 0
    ) {
      normalized.expectedVersion = normalized.version;
    }
    if (
      field.name === 'expectedUpdatedAt' &&
      normalized.expectedUpdatedAt === undefined &&
      (typeof normalized.updatedAt === 'string' || normalized.updatedAt instanceof Date)
    ) {
      normalized.expectedUpdatedAt =
        normalized.updatedAt instanceof Date
          ? normalized.updatedAt.toISOString()
          : normalized.updatedAt;
    }
    if (normalized[field.name] === null || normalized[field.name] === undefined) {
      normalized[field.name] = defaultValues[field.name];
    }
    if (field.fieldType === 'date' || field.fieldType === 'datetime') {
      normalized[field.name] = normalizeDateValueForInput(
        normalized[field.name],
        field.fieldType,
      );
    }
  }
  return normalized;
}

/** Apply the same input normalization to initial loads and authoritative server returns. */
export function normalizeFormValuesForRuntime(
  fields: FormDescriptor['fields'],
  defaultValues: Record<string, unknown>,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const transformed = normalizeInitialFormValues(fields, defaultValues, values);
  for (const field of fields) {
    if (field.value?.inputTransformId && transformed[field.name] !== undefined) {
      transformed[field.name] = applyInputTransform(
        transformed[field.name],
        field.value.inputTransformId,
      );
    }
  }
  return { ...defaultValues, ...transformed };
}

export function useFormSubmission({
  contract,
  formMethods,
  resolvedActions,
  fieldStates,
  onSubmit,
  onAction,
  serverErrors,
  initialValues,
  recordVersion,
  defaultValues,
  loadingInitialValues,
  formValuesWithIds,
}: UseFormSubmissionOptions): UseFormSubmissionResult {
  const { trigger, getValues, setError, clearErrors, reset } = formMethods;
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const [submissionErrors, setSubmissionErrors] = useState<
    UseFormSubmissionResult['submissionErrors']
  >({
    fieldErrors: {},
    globalErrors: [],
    submissionError: null,
    conflict: null,
  });

  const submitGuardRef = useRef(false);
  const lastSubmittedActionIdRef = useRef<string | null>(null);
  const lastSubmittedValuesRef = useRef<Record<string, unknown> | null>(null);
  const lastAppliedRecordVersionRef = useRef<number | undefined>(undefined);
  const lastAppliedInitialValuesKeyRef = useRef<string | null>(null);
  const lastServerValuesRef = useRef<Record<string, unknown> | null>(null);
  const initialResetValuesRef = useRef<Record<string, unknown>>(defaultValues);

  const initialValuesKey = JSON.stringify(initialValues ?? null);

  useEffect(() => {
    if (loadingInitialValues) return;
    if (!initialValues && lastServerValuesRef.current === null) return;
    if (
      initialValuesKey === lastAppliedInitialValuesKeyRef.current &&
      recordVersion === lastAppliedRecordVersionRef.current
    ) return;

    const source = initialValues ?? {};
    const runtimeValues = normalizeFormValuesForRuntime(contract.fields, defaultValues, source);
    initialResetValuesRef.current = runtimeValues;
    reset(runtimeValues);
    lastAppliedInitialValuesKeyRef.current = initialValuesKey;
    lastAppliedRecordVersionRef.current = recordVersion;
  }, [
    initialValues,
    initialValuesKey,
    loadingInitialValues,
    recordVersion,
    reset,
    defaultValues,
    contract.fields,
  ]);

  useEffect(() => {
    if (submissionState !== 'succeeded') return;
    if (!lastSubmittedActionIdRef.current) return;

    const actionId = lastSubmittedActionIdRef.current;
    const resolved = resolvedActions.find(ra => ra.action.id === actionId);
    const resetBehavior = resolved?.resetBehavior ?? 'toInitialValues';

    if (resetBehavior === 'toInitialValues') {
      reset(initialResetValuesRef.current);
    } else if (resetBehavior === 'toSubmittedValues' && lastSubmittedValuesRef.current) {
      reset(lastSubmittedValuesRef.current);
    } else if (resetBehavior === 'toServerValues' && lastServerValuesRef.current) {
      reset(lastServerValuesRef.current);
    }

    lastSubmittedActionIdRef.current = null;
    lastServerValuesRef.current = null;
  }, [submissionState, resolvedActions, reset]);

  useEffect(() => {
    if (!serverErrors) return;
    const nextFieldErrors: Record<string, FormErrorDescriptor> = {};
    const nextGlobalErrors: FormErrorDescriptor[] = [];
    if (Array.isArray(serverErrors)) {
      for (const err of serverErrors) {
        if (err && typeof err === 'object' && 'code' in err) {
          nextGlobalErrors.push(sanitizeFormErrorDescriptor(err));
        }
      }
    } else {
      for (const [fieldName, message] of Object.entries(serverErrors)) {
        const field = contract.fields.find(
          f => f.name === fieldName || (f.id as string) === fieldName
        );
        const descriptor: FormErrorDescriptor = { code: canonicalFormErrorKey(message) };
        if (fieldName === '__global' || fieldName === '_global') {
          nextGlobalErrors.push(descriptor);
          continue;
        }
        const rhfName = field?.name ?? fieldName;
        nextFieldErrors[rhfName] = descriptor;
        setError(rhfName, {
          type: 'server',
          message: String(descriptor.code),
        });
      }
    }
    setSubmissionErrors({
      fieldErrors: nextFieldErrors,
      globalErrors: nextGlobalErrors,
      submissionError: nextGlobalErrors[0] ?? null,
      conflict: null,
    });
  }, [serverErrors, contract.fields, setError]);

  const prevFormValuesRef = useRef<Record<string, unknown>>(formValuesWithIds);

  useEffect(() => {
    const prev = prevFormValuesRef.current;
    prevFormValuesRef.current = formValuesWithIds;

    if (serverErrors) return;
    if (Object.keys(submissionErrors.fieldErrors).length === 0) return;

    const changedFields = Object.keys(formValuesWithIds).filter(
      key => formValuesWithIds[key] !== prev[key]
    );
    if (changedFields.length === 0) return;

    const fieldsToClear = changedFields.filter(f => f in submissionErrors.fieldErrors);
    if (fieldsToClear.length === 0) return;

    clearErrors(fieldsToClear as never);
    setSubmissionErrors(current => {
      const fieldErrors = { ...current.fieldErrors };
      for (const f of fieldsToClear) delete fieldErrors[f];
      return { ...current, fieldErrors };
    });
  }, [formValuesWithIds, serverErrors, submissionErrors.fieldErrors, clearErrors]);

  const submitAction = useCallback(
    async (actionId: string, executionContext?: ActionExecutionContext) => {
      const resolved = resolvedActions.find(ra => ra.action.id === actionId);
      if (!resolved || submitGuardRef.current || !resolved.commandId) return;
      const commandId = resolved.commandId;

      setSubmissionErrors({ fieldErrors: {}, globalErrors: [], submissionError: null, conflict: null });
      clearErrors();

      const isValid = resolved.validatesForm ? await trigger() : true;
      if (!isValid) {
        setSubmissionState('failed');
        return;
      }

      if (!onSubmit) {
        setSubmissionState('idle');
        return;
      }

      const values = getValues();
      const formPayload = buildFormPayload(values, contract, {
        includeFields: resolved.includeFields,
        excludeFields: resolved.excludeFields,
        payloadBuilderId: resolved.payloadBuilderId,
        payloadSchemaRef: resolved.action.payloadSchemaRef,
        fieldStates,
      });
      const payload = {
        ...formPayload,
        ...(executionContext?.confirmationValues ?? {}),
      };

      lastSubmittedValuesRef.current = { ...payload };
      lastSubmittedActionIdRef.current = actionId;
      setSubmissionState('pending');
      submitGuardRef.current = true;

      try {
        const ctx: FormSubmissionContext = {
          actionId,
          commandId,
          recordVersion,
          resetBehavior: resolved.resetBehavior,
        };
        const result = await onSubmit(commandId, payload, ctx);

        if (result?.status === 'succeeded') {
          setSubmissionState('succeeded');
          setSubmissionErrors({ fieldErrors: {}, globalErrors: [], submissionError: null, conflict: null });
          if (result.recordVersion !== undefined) {
            lastAppliedRecordVersionRef.current = result.recordVersion;
          }
          if (result.returnedValues) {
            lastServerValuesRef.current = normalizeFormValuesForRuntime(
              contract.fields,
              defaultValues,
              result.returnedValues,
            );
          }
        } else if (result?.status === 'failed') {
          setSubmissionState('failed');
          const fieldErrors = Object.fromEntries(
            Object.entries(result.fieldErrors ?? {}).map(([field, descriptor]) => [
              field,
              sanitizeFormErrorDescriptor(descriptor),
            ]),
          );
          const globalErrors = (result.globalErrors ?? []).map(sanitizeFormErrorDescriptor);
          setSubmissionErrors({
            fieldErrors,
            globalErrors,
            submissionError: globalErrors[0] ?? null,
            conflict: result.conflict ?? null,
          });
          for (const [fieldIdOrName, descriptor] of Object.entries(fieldErrors)) {
            const field = contract.fields.find(
              f => (f.id as string) === fieldIdOrName || f.name === fieldIdOrName
            );
            const rhfName = field?.name ?? fieldIdOrName;
            setError(rhfName, { type: 'server', message: String(descriptor.code) });
          }
        } else {
          setSubmissionState('failed');
          const errorDescriptor: FormErrorDescriptor = { code: UNEXPECTED_ERROR_KEY };
          setSubmissionErrors({
            fieldErrors: {},
            globalErrors: [errorDescriptor],
            submissionError: errorDescriptor,
            conflict: null,
          });
        }
      } catch (err) {
        setSubmissionState('failed');
        const errorDescriptor: FormErrorDescriptor = {
          code: UNEXPECTED_ERROR_KEY,
          correlationId:
            typeof err === 'object' && err && 'correlationId' in err
              ? String((err as { correlationId?: unknown }).correlationId)
              : undefined,
          retryable:
            typeof err === 'object' && err && 'retryable' in err
              ? Boolean((err as { retryable?: unknown }).retryable)
              : undefined,
        };
        setSubmissionErrors({
          fieldErrors: {},
          globalErrors: [errorDescriptor],
          submissionError: errorDescriptor,
          conflict: null,
        });
      } finally {
        submitGuardRef.current = false;
      }
    },
    [
      resolvedActions,
      trigger,
      getValues,
      onSubmit,
      fieldStates,
      contract,
      recordVersion,
      setError,
      clearErrors,
      defaultValues,
    ]
  );

  const handleAction = useCallback(
    async (action: ActionDescriptor, executionContext?: ActionExecutionContext) => {
      const resolved = resolvedActions.find(ra => ra.action.id === action.id);
      if (!resolved) return;
      if (resolved.isFormSubmit) await submitAction(action.id, executionContext);
      else await onAction?.(action, executionContext);
    },
    [resolvedActions, submitAction, onAction]
  );

  const clearConflict = useCallback(() => {
    setSubmissionState('idle');
    setSubmissionErrors({ fieldErrors: {}, globalErrors: [], submissionError: null, conflict: null });
  }, []);

  return { submitAction, handleAction, submissionState, submissionErrors, clearConflict };
}
